import { cn, EmptyState, Label, Note, SegmentedTabs, Select, StatusBadge } from "@platform/ui";
import { Inbox } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslations } from "use-intl";
import { formatAge, formatDateTime } from "../lib/i18n";
import { DECISION_TARGET_HOURS, targetState } from "../lib/targets";

export type Assignee = "any" | "me" | "unassigned";

/**
 * A work queue's toolbar (ADM-2, card 114): which cases, as a segmented control, and whose, as a select labelled
 * "Assigned". Verification, product review, and disputes share it.
 */
export function QueueToolbar<S extends string>({
  id,
  states,
  state,
  onState,
  stateName,
  assignee,
  onAssignee,
}: {
  id: string;
  states: readonly S[];
  state: S;
  onState: (state: S) => void;
  stateName: (state: S) => string;
  assignee: Assignee;
  onAssignee: (assignee: Assignee) => void;
}) {
  const t = useTranslations();
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <SegmentedTabs
        label={t("admin.queue.state")}
        value={state}
        onChange={(next) => onState(next as S)}
        items={states.map((each) => ({ id: each, label: stateName(each) }))}
      />
      <div className="flex items-center gap-2">
        <Label htmlFor={`${id}-assignee`}>{t("admin.queue.assignee")}</Label>
        <Select
          id={`${id}-assignee`}
          className="w-40"
          value={assignee}
          onChange={(event) => onAssignee(event.target.value as Assignee)}
        >
          {(["any", "me", "unassigned"] as const).map((option) => (
            <option key={option} value={option}>
              {t(`admin.queue.${option}`)}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

/** A queue's table in its frame, or "loading" and the empty state in its place. */
export function QueueFrame({
  rows,
  empty,
  children,
}: {
  rows: readonly unknown[] | undefined;
  /** The empty state's title, when the queue's own words say it better than "Nothing waiting". */
  empty?: string | undefined;
  children: ReactNode;
}) {
  const t = useTranslations();
  return (
    <div className="overflow-hidden rounded-(--radius-card) border border-border bg-surface">
      {rows === undefined ? (
        <p className="px-4 py-3 text-small text-ink-muted">{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <EmptyState icon={Inbox} title={empty ?? t("admin.emptyQueue")} />
      ) : (
        children
      )}
    </div>
  );
}

/**
 * How long a case has waited against the 24-hour decision target (card 117): the hours waited and left, in attention
 * text in the last 4 hours, an attention badge once overdue, and "Waiting for Sumsub" before the clock starts. A
 * decided case shows when it was decided instead.
 */
export function TargetCell({
  startedAt,
  decidedAt,
}: {
  startedAt: string | null | undefined;
  decidedAt?: string | null | undefined;
}) {
  const t = useTranslations();
  if (decidedAt !== null && decidedAt !== undefined)
    return (
      <span className="tabular-nums" title={formatDateTime(decidedAt)}>
        {formatAge(decidedAt)}
      </span>
    );
  const state = targetState(startedAt);
  const title = startedAt === null || startedAt === undefined ? undefined : formatDateTime(startedAt);
  switch (state.kind) {
    case "notStarted":
      return <span className="text-ink-muted">{t("admin.target.notStarted")}</span>;
    case "overdue":
      return (
        <span title={title}>
          <StatusBadge label={t("admin.target.overdue", { hours: state.waited })} tone="attention" />
        </span>
      );
    default:
      return (
        <span
          title={title}
          className={cn("tabular-nums", state.kind === "dueSoon" && "font-medium text-tone-attention-fg")}
        >
          {t("admin.target.waited", { hours: state.waited })}
          <span className={state.kind === "dueSoon" ? undefined : "text-ink-muted"}>
            {` · ${t("admin.target.left", { hours: state.left })}`}
          </span>
        </span>
      );
  }
}

/** The case panel's word once its case is past the decision target (card 117); nothing before. */
export function OverdueNote({
  startedAt,
  inset = true,
}: {
  startedAt: string | null | undefined;
  inset?: boolean;
}) {
  const t = useTranslations();
  const state = targetState(startedAt);
  if (state.kind !== "overdue") return null;
  const note = (
    <Note tone="attention" role="status">
      {t("admin.target.overdueNote", { hours: state.waited, target: DECISION_TARGET_HOURS })}
    </Note>
  );
  return inset ? <div className="border-b border-border-subtle px-5 py-3">{note}</div> : note;
}
